import { n as DEFAULT_ARCHIVE_LIMITS, t as ArchiveLimitError } from "../archive-limits-CMo2zcn-.js";
import { LocalCacheByteSource, requestPersistentStorage } from "./local-cache-byte-source.js";
import { SwitchableByteSource } from "./byte-source.js";
import { decideFallback, isDefinitivelyGone } from "./range-probe.js";
import { a as readResponseBodyCapped, i as probeRemote, n as RemoteRangeByteSource, r as fetchRemoteValidators, t as RangeIgnoredError } from "../remote-range-byte-source-DW-eEL4J.js";
import { StructuralReadError } from "./structural-read-error.js";
import { normalizeShareUrl } from "./share-link.js";
//#region ../src/storage/open-remote-archive.ts
/**
* The conductor of range-based archive streaming: one call that runs
* normalize → cache lookup (revalidated) → probe → fallback decision →
* byte-source construction, plus the background warm-download that swaps a
* ranged session onto a local copy mid-flight.
*
* Every consumer would otherwise hand-roll this dance from the building
* blocks (`share-link`, `range-probe`, `remote-range-byte-source`,
* `local-cache-byte-source`, `byte-source`); this module is the one place the
* policy lives. It is archive-format-agnostic — zip.js enters only where a
* caller wraps `opened.source` in a `ByteSourceReader`.
*
* Cache freshness: a cached copy is compared against the live file via one
* lightweight HEAD — ETag when readable, else the CORS-safelisted
* Last-Modified, else the size. A failed HEAD (offline, HEAD-refusing host)
* serves the cache: availability over freshness, because the alternative
* strands an offline visitor who HAS the bytes. A stale copy is evicted and
* the open proceeds remote.
*/
/** A probe-level rejection, carrying the machine-readable cause. */
var OpenRemoteArchiveError = class extends Error {
	name = "OpenRemoteArchiveError";
	rejectCause;
	/** `options.cause` carries the underlying error where one explains the
	*  rejection in plain words (the `ArchiveLimitError` of `'too-large'`). */
	constructor(message, rejectCause, options) {
		super(message, options);
		this.rejectCause = rejectCause;
	}
};
/** The probe GET budget already covers a slow full download (see
*  remote-range-byte-source); the explicit full-download path uses the same. */
const FULL_DOWNLOAD_TIMEOUT_MS = 3e5;
async function openRemoteArchive(rawUrl, options = {}) {
	const fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
	const url = normalizeShareUrl(rawUrl, {
		googleDriveApiKey: options.googleDriveApiKey,
		corsProxyBaseUrl: options.corsProxyBaseUrl
	});
	const store = options.cacheStore;
	const fromCache = await tryOpenFromCache(url, store, options, fetchImpl);
	if ("opened" in fromCache) return fromCache.opened;
	let probe;
	try {
		probe = await probeRemote(url, fetchImpl, maxArchiveBytesOf(options));
	} catch (err) {
		if (err instanceof ArchiveLimitError) throw tooLarge(url, err);
		if (fromCache.refused !== null) throw tooLarge(url, fromCache.refused);
		const offline = (options.isOnline ?? browserIsOnline)() === false;
		throw new OpenRemoteArchiveError(`opening ${url} failed before any HTTP status (${offline ? "the browser is offline" : "network down or CORS-blocked"}): ${String(err)}`, offline ? "offline" : "cors");
	}
	return openPerDecision(url, probe, fetchImpl, store, options);
}
function maxArchiveBytesOf(options) {
	return options.maxArchiveBytes ?? DEFAULT_ARCHIVE_LIMITS.maxArchiveBytes;
}
/** `navigator.onLine`, or true where there is no navigator (Node). */
function browserIsOnline() {
	return globalThis.navigator?.onLine !== false;
}
function tooLarge(url, err) {
	return new OpenRemoteArchiveError(`opening ${url} refused: ${err.message}`, "too-large", { cause: err });
}
/** The revalidated cache lookup: the opened copy, or why there is none to
*  serve (`refused`: a copy over the cap, kept; null: no usable copy) -
*  either way the caller proceeds to the network. */
async function tryOpenFromCache(url, store, options, fetchImpl) {
	if (store === void 0 || options.skipCache === true) return { refused: null };
	const cached = await store.get(url);
	if (cached === void 0) return { refused: null };
	const maxBytes = maxArchiveBytesOf(options);
	if (cached.blob.size > maxBytes) return { refused: new ArchiveLimitError("archive-bytes", maxBytes, cached.blob.size) };
	if (await isCachedCopyServable(url, cached, fetchImpl)) return { opened: await openLocal(url, cached, store, options, "cache") };
	await store.delete(url);
	return { refused: null };
}
function openPerDecision(url, probe, fetchImpl, store, options) {
	const decision = decideFallback(probe);
	switch (decision.mode) {
		case "ranges": return openRanged(url, decision.size, probe.validators, options, store);
		case "eager-local": return openAccepted(url, {
			blob: new Blob([decision.body]),
			validators: probe.validators
		}, store, options);
		case "full-download": return openFullDownload(url, probe.validators, fetchImpl, store, options);
		case "reject": throw new OpenRemoteArchiveError(`opening ${url} rejected by the range probe (${decision.cause})`, decision.cause);
	}
}
/** Freshness: any readable, differing signal ⇒ stale; no reachable HEAD ⇒
*  serve the cache (availability over freshness, documented above). */
async function isCachedCopyServable(url, cached, fetchImpl) {
	const live = await fetchRemoteValidators(url, fetchImpl);
	if (live === null) return true;
	if (live.kind === "missing") return false;
	return cachedCopyMatchesLive(cached, live);
}
function cachedCopyMatchesLive(cached, live) {
	const cv = cached.validators ?? {};
	const lv = live.validators ?? {};
	if (cv.etag !== void 0 && lv.etag !== void 0) return cv.etag === lv.etag;
	if (cv.lastModified !== void 0 && lv.lastModified !== void 0) return cv.lastModified === lv.lastModified;
	return live.size === null || live.size === cached.blob.size;
}
/** A whole archive downloaded at open (eager or full): offered to
*  `acceptLocalCopy` first, then served and persisted. */
async function openAccepted(url, entry, store, options) {
	await options.acceptLocalCopy?.(entry.blob);
	return openLocal(url, entry, store, options, "network", { persist: true });
}
/** Serve a complete local blob; persist it first when asked to. */
async function openLocal(url, entry, store, options, origin, behavior = {}) {
	if (origin === "network") options.onRead?.({
		origin: "network",
		offset: 0,
		length: entry.blob.size
	});
	if (behavior.persist === true && store !== void 0) {
		await requestPersistentStorage();
		await store.put(url, entry);
	}
	return {
		source: instrument(new LocalCacheByteSource(entry.blob), "cache", options),
		size: entry.blob.size,
		url,
		origin,
		warmed: Promise.resolve(true),
		dispose: () => void 0,
		evict: async () => {
			await store?.delete(url);
		}
	};
}
function openRanged(url, size, validators, options, store) {
	const fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
	const remote = instrument(new RemoteRangeByteSource(url, size, fetchImpl), "network", options);
	const controller = new AbortController();
	const warmController = new AbortController();
	let recoveryDownload = null;
	let evicted = false;
	const runRecoveryDownload = async () => {
		const res = await fetchImpl(url, {
			cache: "no-cache",
			signal: AbortSignal.any([controller.signal, AbortSignal.timeout(FULL_DOWNLOAD_TIMEOUT_MS)])
		});
		if (!res.ok) throw new StructuralReadError(`range-ignore recovery download of ${url} failed (${res.status})`);
		const blob = await readResponseBodyCapped(res, Math.max(1, size)).catch((err) => {
			throw err instanceof ArchiveLimitError ? new StructuralReadError(`range-ignore recovery of ${url} streamed more than the expected ${size} bytes - the file changed mid-session`) : err;
		});
		options.onRead?.({
			origin: "network",
			offset: 0,
			length: blob.size
		});
		if (blob.size !== size) throw new StructuralReadError(`range-ignore recovery downloaded ${blob.size} bytes, expected ${size} — the file changed mid-session`);
		await options.acceptLocalCopy?.(blob);
		const local = instrument(new LocalCacheByteSource(blob), "cache", options);
		if (switchable.switchTo(local) && store !== void 0 && !evicted) {
			await requestPersistentStorage();
			await store.put(url, {
				blob,
				...validators !== void 0 ? { validators } : {}
			});
		}
		return local;
	};
	const recoverToLocal = () => {
		recoveryDownload ??= runRecoveryDownload().catch((err) => {
			recoveryDownload = null;
			throw err;
		});
		return recoveryDownload;
	};
	const switchable = new SwitchableByteSource({
		size,
		read: async (offset, length) => {
			try {
				return await remote.read(offset, length);
			} catch (err) {
				if (!(err instanceof RangeIgnoredError)) throw err;
				return (await recoverToLocal()).read(offset, length);
			}
		}
	});
	const warmed = store !== void 0 && options.warm !== false ? warmToCache(url, validators, fetchImpl, store, switchable, options, AbortSignal.any([controller.signal, warmController.signal]), () => evicted) : Promise.resolve(false);
	return {
		source: switchable,
		size,
		url,
		origin: "network",
		warmed,
		dispose: () => controller.abort(),
		evict: async () => {
			evicted = true;
			warmController.abort();
			await warmed.catch(() => void 0);
			await recoveryDownload?.catch(() => void 0);
			await store?.delete(url);
		}
	};
}
/**
* Background full download → one-shot switch → persist. The order is the
* point: the swap is only persisted when it actually TOOK — `switchTo`
* returning false means the downloaded bytes mismatch the session's size
* (the file changed mid-session, a redirect body) and caching them would
* poison every later visit.
*/
async function warmToCache(url, validators, fetchImpl, store, switchable, options, signal, isEvicted) {
	try {
		await requestPersistentStorage();
		const res = await fetchImpl(url, {
			cache: "no-cache",
			signal: AbortSignal.any([signal, AbortSignal.timeout(FULL_DOWNLOAD_TIMEOUT_MS)])
		});
		if (!res.ok) return false;
		const blob = await readResponseBodyCapped(res, Math.max(1, switchable.size));
		options.onRead?.({
			origin: "network",
			offset: 0,
			length: blob.size
		});
		if (blob.size !== switchable.size) return false;
		await options.acceptLocalCopy?.(blob);
		const local = instrument(new LocalCacheByteSource(blob), "cache", options);
		if (!switchable.switchTo(local)) return false;
		if (isEvicted()) return true;
		await store.put(url, {
			blob,
			...validators !== void 0 ? { validators } : {}
		});
		return true;
	} catch {
		return false;
	}
}
async function openFullDownload(url, validators, fetchImpl, store, options) {
	const res = await fetchImpl(url, {
		cache: "no-cache",
		signal: AbortSignal.timeout(FULL_DOWNLOAD_TIMEOUT_MS)
	});
	if (!res.ok) throw new OpenRemoteArchiveError(`full download of ${url} failed (${res.status})`, isDefinitivelyGone(res.status) ? "missing" : "unusable-link");
	return openAccepted(url, {
		blob: await readResponseBodyCapped(res, maxArchiveBytesOf(options)).catch((err) => {
			throw err instanceof ArchiveLimitError ? tooLarge(url, err) : err;
		}),
		...validators !== void 0 ? { validators } : {}
	}, store, options);
}
/** Wrap a source so every served read reports to the caller's observer. */
function instrument(inner, origin, options) {
	const onRead = options.onRead;
	if (onRead === void 0) return inner;
	return {
		size: inner.size,
		read: async (offset, length) => {
			const bytes = await inner.read(offset, length);
			onRead({
				origin,
				offset,
				length: bytes.length
			});
			return bytes;
		}
	};
}
//#endregion
export { OpenRemoteArchiveError, openRemoteArchive };
